import Container from "@components/Atomic/Container/Container";
import React, { PropsWithChildren } from "react";
import tw from "twin.macro";
import { Breadcrumb } from "../Breadcrumb/Breadcrumb";
import { StyledContainer } from "../Styled/Container";
import { Styles } from "@ts/sanity";
import { styledWithInvalidProps } from "@components/Styled/StyledWithInvalidProps";

interface TitleProps {
  size?: "default" | "44";
}

interface ContainerProps {
  padding?: boolean;
  align?: "left" | "center";
  width?: string;
  styles: Styles[];
}

interface PageTitleProps extends TitleProps, ContainerProps {
  background?: string;
  children?: JSX.Element;
  hideHomepageLink?: boolean;
  noPadding?: boolean;
  paths?: Array<{
    title?: string;
    url?: string;
  }>;
  title: string;
}

const style = {
  size: {
    default: tw`text-[32px] xl:text-[56px]`,
    "44": tw`text-[32px] xl:text-[44px]`,
  },
  align: {
    center: tw`items-center justify-center text-center`,
    left: tw`items-start justify-start text-left`,
  },
};

const InnerWrapper = styledWithInvalidProps(StyledContainer)(
  ({ padding, align }: ContainerProps) => [
    tw`relative flex flex-col`,
    padding ? tw`pt-tera pb-maxi md:pb-mega` : null,
    align ? style.align[align] : style.align.center,
  ],
);

const Title = styledWithInvalidProps("h1")(({ size }: TitleProps) => [
  tw`leading-h1 font-display`,
  size && style.size[size] ? style.size[size] : style.size.default,
]);

const Wrapper = ({
  styles,
  children,
  ...rest
}: PropsWithChildren<ContainerProps>) => {
  if (!!styles?.length) {
    return children;
  }

  return <InnerWrapper {...rest}>{children}</InnerWrapper>;
};

export const PageTitle = ({
  background = "light",
  hideHomepageLink,
  noPadding,
  title,
  size,
  children,
  paths,
  align = "center",
  width,
  styles,
  ...rest
}: PageTitleProps) => {
  return (
    <Container
      styles={styles}
      legacy={!styles?.length}
      background={background}
      {...rest}
    >
      <Wrapper styles={styles} padding={!noPadding} align={align} width={width}>
        {paths?.length ? (
          <Breadcrumb paths={paths} hideHomepageLink={hideHomepageLink} />
        ) : null}
        {title && (
          <Title size={size} data-testid="page-title">
            {title}
          </Title>
        )}
        {children}
      </Wrapper>
    </Container>
  );
};
